import React, { useContext, useState, useEffect, useMemo, useRef } from 'react';
import ProductCard from './ProductCard';
import Loader from '../shared/Loader';
import { ProductContext } from '../../providers/ProductContext';
import { CategoryContext } from '../../providers/CategoryContext';
import { useSearchParams } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import SEO from '../shared/SEO';
import { ProductTagContext } from '../../providers/ProductTagContext';
import { hasProductTag, normalizeProductTag } from '../../config/productTags';
import { getDisplayImageUrl } from '../../utils/imageUtils';


const ProductList = () => {
  const { products, loading: productsLoading, error: productsError, refetchProducts } = useContext(ProductContext);
  const { categories, loading: categoriesLoading } = useContext(CategoryContext);
  const { productTags, tagGroups } = useContext(ProductTagContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showTagQuickAccess, setShowTagQuickAccess] = useState(false);
  const productsSectionRef = useRef(null);
  const pendingMobileScrollRef = useRef(false);
  const productsPerPage = 48;

  // Se leen los parámetros de la URL
  const selectedCategory = searchParams.get('category') || '';
  const selectedSubcategory = searchParams.get('subcategory') || '';
  const selectedTags = (searchParams.get('tags') || searchParams.get('tag') || '')
    .split(',')
    .map(tag => tag.trim())
    .filter(Boolean);
  const selectedTagsKey = selectedTags.map(normalizeProductTag).join('|');

  const categoryProducts = useMemo(() => products.filter(p =>
    (!selectedCategory || p.category?.name === selectedCategory) &&
    (!selectedSubcategory || p.subcategory?.includes(selectedSubcategory))
  ), [products, selectedCategory, selectedSubcategory]);

  const filteredProducts = categoryProducts.filter(p =>
    (!selectedTags.length || selectedTags.every(tag => hasProductTag(p, tag))) &&
    (!searchQuery || [p.name, p.description, p.category?.name, p.subcategory, ...(p.tags || [])]
      .filter(Boolean)
      .some(value => value.toLowerCase().includes(searchQuery.toLowerCase())))
  );

  // Las etiquetas y sus portadas salen de los productos de la categoría y
  // subcategoría, sin reducir las opciones por la búsqueda o la etiqueta activa.
  const availableTagGroups = useMemo(() => (
    tagGroups
      .map(group => ({
        ...group,
        tags: group.tags.reduce((availableTags, tag) => {
          const taggedProducts = categoryProducts.filter(product => hasProductTag(product, tag));

          if (!taggedProducts.length) return availableTags;

          const productWithImage = taggedProducts.find(product => (
            product.images?.some(image => typeof image === 'string' && image.trim()) || product.image
          )) || taggedProducts[0];
          const image = productWithImage.images?.find(item => typeof item === 'string' && item.trim())
            || productWithImage.image;
          const configuredTag = productTags.find(item => normalizeProductTag(item) === normalizeProductTag(tag));

          availableTags.push({
            name: tag,
            image: getDisplayImageUrl(configuredTag?.image || image)
          });
          return availableTags;
        }, [])
      }))
      .filter(group => group.tags.length > 0)
  ), [categoryProducts, productTags, tagGroups]);

  // Calcular paginación
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);
  const startIndex = (currentPage - 1) * productsPerPage;
  const endIndex = startIndex + productsPerPage;
  const currentProducts = filteredProducts.slice(startIndex, endIndex);

  // Resetear a página 1 cuando cambien los filtros o búsqueda
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, selectedSubcategory, selectedTagsKey, searchQuery]);

  useEffect(() => {
    setShowTagQuickAccess(false);
  }, [selectedCategory, selectedSubcategory]);

  const scrollToProducts = () => {
    const behavior = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => productsSectionRef.current?.scrollIntoView({ behavior, block: 'start' }));
    });
  };

  // Función para cambiar de página
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    scrollToProducts();
  };

  if (categoriesLoading || productsLoading) {
    return <Loader />;
  }

  if (productsError && products.length === 0) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '20px' }}>
        <i className="fa-solid fa-exclamation-triangle" style={{ fontSize: '3rem', color: '#ffc107', marginBottom: '16px' }}></i>
        <h3>Error al cargar productos</h3>
        <p style={{ color: '#666', marginBottom: '20px' }}>Hubo un problema de conexión. Intentá de nuevo.</p>
        <button onClick={() => refetchProducts()} className="btn btn-success" style={{ borderRadius: '25px', padding: '10px 30px', fontWeight: '700' }}>
          <i className="fa-solid fa-rotate-right me-2"></i>
          Reintentar
        </button>
      </div>
    );
  }

  // Generar título y descripción SEO dinámicos según filtros
  const getSeoTitle = () => {
    const filters = [...selectedTags, selectedSubcategory, selectedCategory].filter(Boolean);
    return filters.length ? `${filters.join(' - ')} - Catálogo` : 'Tienda - Todos los Productos';
  };

  const getSeoDescription = () => {
    if (selectedTags.length) {
      return `Explora productos de ${selectedTags.join(' y ').toLowerCase()} en todas nuestras categorías. ${filteredProducts.length} opciones disponibles con envío a todo Uruguay.`;
    }
    if (selectedCategory) {
      return `Explora nuestra colección de ${selectedCategory.toLowerCase()}. ${filteredProducts.length} productos disponibles con envío a todo Uruguay.`;
    }
    return `Descubre ${filteredProducts.length} productos únicos en REM Enterprises. Remeras originales, personalizadas y más. Envíos a todo Uruguay.`;
  };

  const applyFilters = (params, closeMobileFilters = false) => {
    setSearchParams(params);

    if (closeMobileFilters) {
      pendingMobileScrollRef.current = true;
      setShowMobileFilters(false);
    } else {
      scrollToProducts();
    }
  };

  const filtersWithTags = (filters = {}) => ({
    ...filters,
    ...(selectedTags.length ? { tags: selectedTags.join(',') } : {})
  });
  const hasActiveFilters = Boolean(selectedCategory || selectedSubcategory || selectedTags.length);

  const toggleTag = (tag, closeMobileFilters = false) => {
    const isActive = selectedTags.some(item => normalizeProductTag(item) === normalizeProductTag(tag));
    setSearchQuery('');
    applyFilters({
      ...(selectedCategory ? { category: selectedCategory } : {}),
      ...(selectedSubcategory ? { subcategory: selectedSubcategory } : {}),
      ...(!isActive ? { tags: tag } : {})
    }, closeMobileFilters);
  };

  const renderTagButtons = (listClassName, closeMobileFilters = false, withImages = false) => (
    availableTagGroups.map(group => (
      <div
        key={group.id}
        className={`product-tag-filter-group product-tag-filter-group-${group.id}`}
        style={withImages ? { '--tag-columns': Math.min(3, group.tags.length) } : undefined}
      >
        <span className="product-tag-filter-group-title">{group.label}</span>
        <div className={listClassName}>
          {group.tags.map(({ name, image }) => {
            const isActive = selectedTags.some(item => normalizeProductTag(item) === normalizeProductTag(name));

            return (
              <button
                key={name}
                type="button"
                className={`product-tag-filter-btn ${withImages ? 'product-tag-filter-btn-image' : ''} ${isActive ? 'active' : ''}`}
                onClick={() => toggleTag(name, closeMobileFilters)}
                aria-pressed={isActive}
              >
                {withImages && <img src={image} alt="" aria-hidden="true" loading="lazy" />}
                {withImages && <span className="product-tag-filter-btn-image-overlay" aria-hidden="true" />}
                <span className="product-tag-filter-btn-label">{name}</span>
              </button>
            );
          })}
        </div>
      </div>
    ))
  );

  const renderFilterControls = (closeMobileFilters = false, includeHeader = true) => (
    <>
      {includeHeader && (
        <div className="sidebar-header">
          <h4>
            <i className="fa-solid fa-filter me-2"></i>
            Filtrar Productos
          </h4>
          {hasActiveFilters && (
            <button
              className="btn-clear-filters"
              onClick={() => applyFilters({}, closeMobileFilters)}
              aria-label="Limpiar filtros"
            >
              <i className="fa-solid fa-times"></i>
            </button>
          )}
        </div>
      )}

      {/* All Products Button */}
      <div className="filter-section">
        <button
          className={`filter-btn-main ${!hasActiveFilters ? 'active' : ''}`}
          onClick={() => applyFilters({}, closeMobileFilters)}
        >
          <i className="fa-solid fa-grid-2 me-2"></i>
          <span>Todos los Productos</span>
          <span className="product-count">{products.length}</span>
        </button>
      </div>

      {/* Categories */}
      <div className="filter-section">
        <h5 className="section-title">
          <i className="fa-solid fa-tags me-2"></i>
          Categorías
        </h5>
        <div className="category-list">
          {categories.map(category => {
            const count = products.filter(p =>
              p.category?.name === category.name &&
              (!selectedTags.length || selectedTags.every(tag => hasProductTag(p, tag)))
            ).length;
            const isActive = selectedCategory === category.name;

            return (
              <div key={category._id} className="category-item">
                <button
                  className={`category-btn ${isActive ? 'active' : ''}`}
                  onClick={() => applyFilters(filtersWithTags({ category: category.name }), closeMobileFilters)}
                >
                  <div className="category-info">
                    <i className={`${category.icon} category-icon`}></i>
                    <span className="category-name">{category.name}</span>
                  </div>
                  <span className="category-count">{count}</span>
                </button>

                {/* Subcategories */}
                {isActive && category.subcategories?.length > 0 && (
                  <div className="subcategory-list">
                    {category.subcategories.map(subcategory => {
                      const subCount = products.filter(p =>
                        p.subcategory?.includes(subcategory.name) &&
                        (!selectedTags.length || selectedTags.every(tag => hasProductTag(p, tag)))
                      ).length;
                      const isSubActive = selectedSubcategory === subcategory.name;

                      return (
                        <button
                          key={subcategory.name}
                          className={`subcategory-btn ${isSubActive ? 'active' : ''}`}
                          onClick={() => applyFilters(filtersWithTags({ category: category.name, subcategory: subcategory.name }), closeMobileFilters)}
                        >
                          <span className="subcategory-name">{subcategory.name}</span>
                          <span className="subcategory-count">{subCount}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="filter-section">
        <h5 className="section-title">
          <i className="fa-solid fa-wand-magic-sparkles me-2"></i>
          Explorar por etiqueta
        </h5>
        <div className="product-tag-filter-groups">
          {renderTagButtons('product-tag-filter-list', closeMobileFilters)}
        </div>
      </div>

      {/* Active Filters */}
      {hasActiveFilters && (
        <div className="active-filters">
          <h6 className="filters-title">
            <i className="fa-solid fa-check-circle me-2"></i>
            Filtros Activos
          </h6>
          <div className="filter-tags">
            {selectedCategory && (
              <span className="filter-tag category-tag">
                <i className="fa-solid fa-tag me-1"></i>
                {selectedCategory}
                <button
                  className="remove-filter"
                  onClick={() => applyFilters(filtersWithTags(selectedSubcategory ? { subcategory: selectedSubcategory } : {}), closeMobileFilters)}
                  aria-label={`Quitar filtro ${selectedCategory}`}
                >
                  <i className="fa-solid fa-times"></i>
                </button>
              </span>
            )}
            {selectedSubcategory && (
              <span className="filter-tag subcategory-tag">
                <i className="fa-solid fa-bookmark me-1"></i>
                {selectedSubcategory}
                <button
                  className="remove-filter"
                  onClick={() => applyFilters(filtersWithTags(selectedCategory ? { category: selectedCategory } : {}), closeMobileFilters)}
                  aria-label={`Quitar filtro ${selectedSubcategory}`}
                >
                  <i className="fa-solid fa-times"></i>
                </button>
              </span>
            )}
            {selectedTags.map(tag => (
              <span key={tag} className="filter-tag product-tag">
                <i className="fa-solid fa-wand-magic-sparkles me-1"></i>
                {tag}
                <button
                  className="remove-filter"
                  onClick={() => applyFilters({
                    ...(selectedCategory ? { category: selectedCategory } : {}),
                    ...(selectedSubcategory ? { subcategory: selectedSubcategory } : {}),
                    ...(selectedTags.filter(item => normalizeProductTag(item) !== normalizeProductTag(tag)).length
                      ? { tags: selectedTags.filter(item => normalizeProductTag(item) !== normalizeProductTag(tag)).join(',') }
                      : {})
                  }, closeMobileFilters)}
                  aria-label={`Quitar filtro ${tag}`}
                >
                  <i className="fa-solid fa-times"></i>
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </>
  );

  return (
    <>
      <SEO 
        title={getSeoTitle()}
        description={getSeoDescription()}
        url={`/tienda${searchParams.toString() ? `?${searchParams.toString()}` : ''}`}
        type="website"
        breadcrumbs={[
          { name: 'Inicio', url: '/' },
          { name: 'Tienda', url: '/tienda' },
          ...(selectedCategory ? [{ name: selectedCategory, url: `/tienda?category=${selectedCategory}` }] : []),
          ...selectedTags.map(tag => ({ name: tag, url: `/tienda?tags=${selectedTags.join(',')}` }))
        ]}
      />
      <div className="store-container">
        {/* Store Header */}
      <div className="store-header">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-8">
              <h1 className="store-title">
                <i className="fa-solid fa-store me-3"></i>
                Nuestra Tienda
              </h1>
              <p className="store-subtitle">
                Descubre productos únicos y de calidad
              </p>
            </div>
            <div className="col-md-4 text-md-end">
              <div className="store-stats">
                <div className="stat-item">
                  <span className="stat-number">{filteredProducts.length}</span>
                  <span className="stat-label">Productos</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">{categories.length}</span>
                  <span className="stat-label">Categorías</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container-fluid">
        {availableTagGroups.length > 0 && (
          <section className={`store-tag-quick-access ${showTagQuickAccess ? '' : 'is-collapsed'}`} aria-labelledby="store-tag-quick-access-title">
            <div className="store-tag-quick-access-heading">
              <div>
                <span className="store-tag-quick-access-kicker">Encontrá tu estilo</span>
                <h2 id="store-tag-quick-access-title">
                  <i className="fa-solid fa-wand-magic-sparkles"></i>
                  Explorá por etiqueta
                </h2>
              </div>
              <div className="store-tag-quick-access-actions">
                {selectedTags.length > 0 && (
                  <button
                    type="button"
                    className="store-tag-quick-access-clear"
                    onClick={() => applyFilters({
                      ...(selectedCategory ? { category: selectedCategory } : {}),
                      ...(selectedSubcategory ? { subcategory: selectedSubcategory } : {})
                    })}
                  >
                    <i className="fa-solid fa-xmark"></i>
                    Limpiar etiquetas
                  </button>
                )}
                <button
                  type="button"
                  className="store-tag-quick-access-toggle"
                  onClick={() => setShowTagQuickAccess(prev => !prev)}
                  aria-controls="store-tag-quick-access-groups"
                  aria-expanded={showTagQuickAccess}
                >
                  <i className={`fa-solid ${showTagQuickAccess ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  {showTagQuickAccess ? 'Ocultar etiquetas' : 'Mostrar etiquetas'}
                </button>
              </div>
            </div>
            <div id="store-tag-quick-access-groups" className="store-tag-quick-access-groups" hidden={!showTagQuickAccess}>
              {renderTagButtons('store-tag-quick-access-list', false, true)}
            </div>
          </section>
        )}
        <div className="mobile-filter-toolbar">
          <button
            className="mobile-filter-trigger"
            onClick={() => setShowMobileFilters(true)}
            aria-haspopup="dialog"
            aria-expanded={showMobileFilters}
          >
            <span>
              <i className="fa-solid fa-filter me-2"></i>
              Filtrar productos
            </span>
            {hasActiveFilters && (
              <span className="mobile-filter-active">
                {selectedTags.length ? selectedTags.join(' + ') : (selectedSubcategory || selectedCategory)}
              </span>
            )}
          </button>
        </div>
        <div className="row">
          {/* Enhanced Sidebar */}
          <div className="col-lg-3 col-md-4 filters-sidebar-column">
            <div className="store-sidebar">
              {renderFilterControls()}
            </div>
          </div>

          {/* Enhanced Products Grid */}
          <div className="col-lg-9 col-md-8">
            <div className="products-section" ref={productsSectionRef}>
              {/* Results Header con Buscador */}
              <div className="results-header-with-search">
                <div className="row align-items-center">
                  {/* Título y contador - 50% */}
                  <div className="col-md-6">
                    <div className="results-info">
                      <h3 className="results-title">
                        {selectedCategory ? (
                          selectedSubcategory ? (
                            <>
                              <i className="fa-solid fa-bookmark me-2"></i>
                              {selectedSubcategory}
                              <span className="breadcrumb-separator">en</span>
                              {selectedCategory}
                            </>
                          ) : (
                            <>
                              <i className="fa-solid fa-tag me-2"></i>
                              {selectedCategory}
                            </>
                          )
                        ) : selectedTags.length ? (
                          <>
                            <i className="fa-solid fa-wand-magic-sparkles me-2"></i>
                            {selectedTags.join(' + ')}
                          </>
                        ) : (
                          <>
                            <i className="fa-solid fa-grid-2 me-2"></i>
                            Todos los Productos
                          </>
                        )}
                      </h3>
                      <p className="results-count" aria-live="polite">
                        {filteredProducts.length === 0 ? (
                          'No se encontraron productos'
                        ) : (
                          <>
                            {`${filteredProducts.length} producto${filteredProducts.length !== 1 ? 's' : ''} encontrado${filteredProducts.length !== 1 ? 's' : ''}`}
                            {totalPages > 1 && (
                              <span className="page-indicator ms-2">
                                (Página {currentPage} de {totalPages})
                              </span>
                            )}
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                  
                  {/* Buscador - 50% */}
                  <div className="col-md-6">
                    <div className="store-search-wrapper">
                      <i className="fa-solid fa-search search-icon"></i>
                      <input
                        type="text"
                        className="store-search-input"
                        placeholder="Buscar productos..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                      {searchQuery && (
                        <button 
                          className="search-clear-btn"
                          onClick={() => setSearchQuery('')}
                          aria-label="Limpiar búsqueda"
                        >
                          <i className="fa-solid fa-times"></i>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Products Grid */}
              <div className="products-grid">
                {filteredProducts.length === 0 ? (
                  <div className="no-products">
                    <div className="no-products-content">
                      <i className="fa-solid fa-search"></i>
                      <h4>No hay productos en esta categoría</h4>
                      <p>Intenta con otra categoría o explora todos nuestros productos</p>
                      <button 
                        className="btn-explore-all"
                        onClick={() => applyFilters({})}
                      >
                        <i className="fa-solid fa-grid-2 me-2"></i>
                        Ver Todos los Productos
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="products-uniform-grid">
                      {currentProducts.map(product => (
                        <div key={product._id} className="product-item-enhanced">
                          <ProductCard product={product} />
                        </div>
                      ))}
                    </div>

                    {/* Paginación */}
                    {totalPages > 1 && (
                      <div className="ecommerce-pagination">
                        <button 
                          className="pagination-btn prev-btn"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          <i className="fa-solid fa-chevron-left"></i>
                          <span>Anterior</span>
                        </button>

                        <div className="pagination-numbers">
                          {[...Array(totalPages)].map((_, index) => {
                            const pageNumber = index + 1;
                            // Mostrar solo algunas páginas alrededor de la actual
                            if (
                              pageNumber === 1 ||
                              pageNumber === totalPages ||
                              (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                            ) {
                              return (
                                <button
                                  key={pageNumber}
                                  className={`pagination-number ${currentPage === pageNumber ? 'active' : ''}`}
                                  onClick={() => handlePageChange(pageNumber)}
                                >
                                  {pageNumber}
                                </button>
                              );
                            } else if (
                              pageNumber === currentPage - 2 ||
                              pageNumber === currentPage + 2
                            ) {
                              return <span key={pageNumber} className="pagination-dots">...</span>;
                            }
                            return null;
                          })}
                        </div>

                        <button 
                          className="pagination-btn next-btn"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          <span>Siguiente</span>
                          <i className="fa-solid fa-chevron-right"></i>
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        show={showMobileFilters}
        onHide={() => setShowMobileFilters(false)}
        onExited={() => {
          if (pendingMobileScrollRef.current) {
            pendingMobileScrollRef.current = false;
            scrollToProducts();
          }
        }}
        fullscreen
        className="mobile-filter-modal"
        contentClassName="mobile-filter-modal-content"
        aria-labelledby="mobile-filter-modal-title"
      >
        <Modal.Header closeButton>
          <Modal.Title id="mobile-filter-modal-title">
            <i className="fa-solid fa-filter me-2"></i>
            Filtrar Productos
          </Modal.Title>
          {hasActiveFilters && (
            <button
              className="mobile-filter-clear"
              onClick={() => applyFilters({}, true)}
            >
              Limpiar
            </button>
          )}
        </Modal.Header>
        <Modal.Body>
          <div className="mobile-filter-content">
            {renderFilterControls(true, false)}
          </div>
        </Modal.Body>
      </Modal>
      </div>
    </>
  );
};

export default ProductList;
